import { LoadingOutlined } from '@ant-design/icons';
import { Button, message } from 'antd';
import { RecordNameModalDialog } from './RecordNameModalDialog';
import {
  DeviceValue,
  handleCommitPartitions,
  newRecord,
  updateHumidityCalibrantRefKey,
  updateInstensityScalingFactor,
  updateRecordBoundaries,
  updateRecordDescription,
  updateRecordHumidityCalibrantReferenceKey,
  updateRecordName,
} from '../../../services/cache/idb';
import { useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { FlexCol } from '../../../components/common/common';
import { SenseMode } from '../../../types/types';
import { computeAndSetHumidityCalibrant, uploadRecord } from '../../ModelsManagementPage/Records/RecordsManagement';
import { getUserSub } from '../../../utils/helpers/utils';
import { useAuth0, User } from '@auth0/auth0-react';
import { useMessageContext } from '../../../state/context/MessageContext';
import { CSM_PROTOCOL_COMMAND_TYPE, encodeBuzzerFrequencyAndDuration, PERIPHERAL_ADDRESS } from '../../../components/serial/csm';
import { OdorResultsModal } from './OdorResultsModal';
import { message as antdMessage } from 'antd';
import { loadHumidityCalibrant, loadWoundOdorDisplayEnabled } from '../../../services/cache/localStorage';

type RecordingControllerProps = {
  senseMode: SenseMode;
  recordKeyRef: React.MutableRefObject<string | null>;
  decimatedMZIPartitionSeriesRef: React.MutableRefObject<number[][]>;
  decimatedTimestampPartitionSeriesRef: React.MutableRefObject<number[]>;
  decimatedHumidityPartitionSeriesRef: React.MutableRefObject<number[]>;
  decimatedTemperatureSeriesRef: React.MutableRefObject<number[]>;
  setIsRecording: React.Dispatch<React.SetStateAction<boolean>>;
  isRecording: boolean;
  deviceValue: DeviceValue | null;
  isAutoSavedMeasurementEnabled: boolean;
  isRecordingCalibrantEnabled: boolean;
  onResetMzi: () => void;
  measurementName: string;
  descriptionName: string;
  baselineDuration: number;
  analyteDuration: number;
  desorptionDuration: number;
  frameCounterRef: React.MutableRefObject<number>;
  isOdorResultDisplayed: boolean;
  setIsOdorResultDisplayed: React.Dispatch<React.SetStateAction<boolean>>;
  multiplicatorFactor: number;
  setMultiplicatorFactor: React.Dispatch<React.SetStateAction<number>>;
};

const RecordingController: React.FC<RecordingControllerProps> = (props) => {
  const {
    senseMode,
    recordKeyRef,
    decimatedMZIPartitionSeriesRef,
    decimatedTimestampPartitionSeriesRef,
    decimatedHumidityPartitionSeriesRef,
    decimatedTemperatureSeriesRef,
    setIsRecording,
    isRecording,
    deviceValue,
    isAutoSavedMeasurementEnabled,
    isRecordingCalibrantEnabled,
    onResetMzi: onResetMzi,
    measurementName,
    descriptionName,
    baselineDuration,
    analyteDuration,
    desorptionDuration,
    frameCounterRef,
    isOdorResultDisplayed,
    setIsOdorResultDisplayed,
    multiplicatorFactor,
    setMultiplicatorFactor,
  } = props;

  const { addCSMCommand, initialMmiPartitionKey } = useMessageContext();

  // auth0 things
  const { user, isAuthenticated, getAccessTokenSilently } = useAuth0();
  const [userInfo, setUserInfo] = useState<User | null>(null);
  const [accessToken, setAccessToken] = useState<string>();

  const [isRecordModalOpen, setIsRecordModalOpen] = useState<boolean>(false);
  const [recordStartTimestamp, setRecordStartTimestamp] = useState<number>(0);
  const [elapsedMs, setElapsedMs] = useState<number>(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const [phaseAlertsShown, setPhaseAlertsShown] = useState({
    baseline: false,
    analyte: false,
    desorption: false,
    finished: false,
  });

  // states for record idx baseline and analyte definition
  const [baselineStartIdxState, setBaselineStartIdxState] = useState<number>(0);
  const [baselineStopIdxState, setBaselineStopIdxState] = useState<number>(0);
  const [analyteStartIdxState, setAnalyteStartIdxState] = useState<number>(0);
  const [analyteStopIdxState, setAnalyteStopIdxState] = useState<number>(0);

  const startRecording = async () => {
    onResetMzi();
    try {
      if (deviceValue === null) {
        throw new Error('device value is null');
      }
      let recordKey = await newRecord(deviceValue, initialMmiPartitionKey);
      console.info('recording controller: start recording: record key', recordKey);
      recordKeyRef.current = recordKey;
      decimatedMZIPartitionSeriesRef.current = [];
      decimatedTimestampPartitionSeriesRef.current = [];
      decimatedHumidityPartitionSeriesRef.current = [];
      decimatedTemperatureSeriesRef.current = [];
      setIsRecording(true);
      setRecordStartTimestamp(Date.now());
    } catch (e: any) {
      console.log('recording controller: could not start recording', e);
      if (e && e.message) {
        message.error('Could not start recording: ' + e);
      }
    }
  };

  const stopRecording = async () => {
    if (recordKeyRef.current === null) {
      console.log('recording controller: record key is null');
      return;
    }
    // console.info('recording controller: stop recording: record key', recordKeyRef.current);
    // console.log('recording controller: stop recording: about to commit the last partition', decimatedMZIPartitionSeriesRef.current);
    handleCommitPartitions(recordKeyRef.current, decimatedTimestampPartitionSeriesRef.current, decimatedMZIPartitionSeriesRef.current, decimatedHumidityPartitionSeriesRef.current, decimatedTemperatureSeriesRef.current);
    decimatedMZIPartitionSeriesRef.current = [];
    decimatedTimestampPartitionSeriesRef.current = [];
    decimatedHumidityPartitionSeriesRef.current = [];
    decimatedTemperatureSeriesRef.current = [];

    setIsRecording(false);
    if (senseMode === SenseMode.Recording) setIsRecordModalOpen(true);
    setRecordStartTimestamp(0);
    frameCounterRef.current = 0; // reset counter of frames.

    setPhaseAlertsShown({
      baseline: false,
      analyte: false,
      desorption: false,
      finished: false,
    });
    setElapsedMs(0);
  };

  const onOkTimedRecord = async () => {
    if (recordKeyRef.current === null) {
      console.log('record name modal dialog: record key is null');
      return;
    }
    await stopRecording();
    await updateRecordBoundaries(recordKeyRef.current, baselineStartIdxState, baselineStopIdxState, analyteStartIdxState, analyteStopIdxState);
    try {
      // console.log('about to update name of the record...', recordKeyRef.current);
      await updateRecordName(recordKeyRef.current, measurementName);
    } catch (e: any) {
      if (e && e.message) {
        message.error(`Could not update record name: ${e.message}`);
      }
      return;
    }
    try {
      // console.log('about to update name of the record...', recordKeyRef.current);
      await updateInstensityScalingFactor(recordKeyRef.current, multiplicatorFactor);
    } catch (e: any) {
      if (e && e.message) {
        message.error(`Could not update intensity scaling factor: ${e.message}`);
      }
      return;
    }
    try {
      await updateRecordDescription(recordKeyRef.current, descriptionName);
    } catch (e: any) {
      if (e && e.message) {
        message.error(`Could not update record description: ${e.message}`);
      }
      return;
    }

    // upload record auto
    if (isAutoSavedMeasurementEnabled && userInfo?.sub && accessToken) {
      await uploadRecord(getUserSub(userInfo?.sub), recordKeyRef.current, accessToken);
    }

    message.success(
      <>
        Saved the record under name: <b>{measurementName}</b>!
      </>
    );

    if (isRecordingCalibrantEnabled) {
      // assign this record into the active current humidity calibrant

      try {
        await updateHumidityCalibrantRefKey(recordKeyRef.current);
      } catch (e: any) {
        if (e && e.message) {
          message.error(`Could not HumidityCalibrant description: ${e.message}`);
        }
        return;
      }
      // also activate this humid record
      try {
        await computeAndSetHumidityCalibrant(recordKeyRef.current);

        antdMessage.success(`Successfully saved "${measurementName}" as humidity calibrant`);
      } catch (e: any) {
        console.error(e);
        antdMessage.error('Failed to save humidity calibrant: ' + e.message);
      }
      setIsOdorResultDisplayed(false); // not relevant
    } else {
      let woundDisplayEnabled = loadWoundOdorDisplayEnabled();
      console.log('woundDisplayEnabled', woundDisplayEnabled);
      // linking active humidity calibrant record ID to the current record

      if (woundDisplayEnabled) {
        try {
          let humidityreferenceKey = loadHumidityCalibrant();
          console.log('humidityreferenceKey', humidityreferenceKey);
          if (humidityreferenceKey !== undefined) {
            await updateRecordHumidityCalibrantReferenceKey(recordKeyRef.current, humidityreferenceKey.recordKey);
          }
        } catch (e: any) {
          if (e && e.message) {
            message.error(`Could not HumidityCalibrant description: ${e.message}`);
          }
          return;
        }
        setIsOdorResultDisplayed(true);
        console.info('changed state of display');
      } else {
        setIsOdorResultDisplayed(false);
      }
    }
  };

  const handleClickRecordingButton = () => {
    if (isRecording && senseMode === SenseMode.TimedRecording) {
      onOkTimedRecord();
    } else if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  const renderElapsedTime = () => {
    let elapsedSeconds = Math.floor(elapsedMs / 1000);
    let elapsedMinutes = Math.floor(elapsedSeconds / 60);
    let elapsedHours = Math.floor(elapsedMinutes / 60);
    let elapsedMinutesMod = elapsedMinutes % 60;
    let elapsedSecondsMod = elapsedSeconds % 60;
    let elpasedString = '';
    if (elapsedHours > 0) {
      elpasedString += `${elapsedHours}h `;
    }
    if (elapsedMinutes > 0) {
      elpasedString += `${elapsedMinutesMod}m `;
    }
    if (elapsedSeconds > 0) {
      elpasedString += `${elapsedSecondsMod}s`;
    }
    return elpasedString;
  };

  useEffect(() => {
    getAccessTokenSilently().then((token) => {
      setAccessToken(token);
    });
  }, [getAccessTokenSilently]);

  useEffect(() => {
    if (!user || !isAuthenticated) setUserInfo(null);
    else setUserInfo(user);
  }, [isAuthenticated, user]);

  useEffect(() => {
    // start timer which will get disabled only at end of recording
    if (isRecording) {
      intervalRef.current = setInterval(() => {
        const elapsed = Date.now() - recordStartTimestamp;
        setElapsedMs(elapsed);
      }, 100); // Update every 100ms
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isRecording]);

  // Phase monitoring useEffect
  useEffect(() => {
    if (senseMode !== SenseMode.TimedRecording) {
      return;
    }

    // Convert durations from seconds to milliseconds for comparison
    const baselineDurationMs = baselineDuration * 1000;
    const analyteDurationMs = analyteDuration * 1000;
    const desorptionDurationMs = desorptionDuration * 1000;

    const totalBaselineAnalyteMs = baselineDurationMs + analyteDurationMs;
    const totalDurationMs = baselineDurationMs + analyteDurationMs + desorptionDurationMs;

    if (elapsedMs > 0 && elapsedMs < baselineDurationMs && !phaseAlertsShown.baseline) {
      message.info('Measurement started, baseline measurement...');
      setPhaseAlertsShown((prev) => ({ ...prev, baseline: true }));
    } else if (elapsedMs >= baselineDurationMs && elapsedMs < totalBaselineAnalyteMs && !phaseAlertsShown.analyte) {
      message.info('Bring device on the target');
      setPhaseAlertsShown((prev) => ({ ...prev, analyte: true }));
      addCSMCommand({
        id: uuidv4().toString(),
        message: {
          PeripheralType: PERIPHERAL_ADDRESS.BUZZER,
          CmdType: CSM_PROTOCOL_COMMAND_TYPE.AccessorySetCmd,
          Payload: encodeBuzzerFrequencyAndDuration(500, 100),
        },
      });
      addCSMCommand({
        id: uuidv4().toString(),
        message: {
          PeripheralType: PERIPHERAL_ADDRESS.BUZZER,
          CmdType: CSM_PROTOCOL_COMMAND_TYPE.AccessorySetCmd,
          Payload: encodeBuzzerFrequencyAndDuration(1000, 100),
        },
      });
      // only mark where baseline ends; analyte start/stop are computed later,
      // once we know the full analyte phase length
      setBaselineStopIdxState(frameCounterRef.current);
    } else if (elapsedMs >= totalBaselineAnalyteMs && elapsedMs < totalDurationMs && !phaseAlertsShown.desorption) {
      message.info('Remove device, desorption started...');
      addCSMCommand({
        id: uuidv4().toString(),
        message: {
          PeripheralType: PERIPHERAL_ADDRESS.BUZZER,
          CmdType: CSM_PROTOCOL_COMMAND_TYPE.AccessorySetCmd,
          Payload: encodeBuzzerFrequencyAndDuration(1000, 100),
        },
      });
      addCSMCommand({
        id: uuidv4().toString(),
        message: {
          PeripheralType: PERIPHERAL_ADDRESS.BUZZER,
          CmdType: CSM_PROTOCOL_COMMAND_TYPE.AccessorySetCmd,
          Payload: encodeBuzzerFrequencyAndDuration(500, 100),
        },
      });
      setPhaseAlertsShown((prev) => ({ ...prev, desorption: true }));

      // Full analyte phase spans from (baselineStopIdxState + 1) to frameCounterRef.current.
      // Keep only the last third of that range as the "analyte" record boundaries.
      const analyteStopIdx: number = frameCounterRef.current;
      const analyteRangeStartIdx: number = baselineStopIdxState + 1;
      const totalAnalyteFrames: number = analyteStopIdx - analyteRangeStartIdx + 1;
      const lastThirdFrameCount: number = Math.floor(totalAnalyteFrames / 3);
      const analyteStartIdx: number = analyteStopIdx - lastThirdFrameCount + 1;

      setAnalyteStartIdxState(analyteStartIdx);
      setAnalyteStopIdxState(analyteStopIdx);
    } else if (elapsedMs >= totalDurationMs && !phaseAlertsShown.finished) {
      message.success('Measurement finished');
      setPhaseAlertsShown((prev) => ({ ...prev, finished: true }));
      // stop recording
      // console.log('record boundaries are:', baselineStartIdxState, baselineStopIdxState, analyteStartIdxState, analyteStopIdxState);
      if (recordKeyRef.current) onOkTimedRecord();
    }
  }, [elapsedMs, baselineDuration, analyteDuration, desorptionDuration, phaseAlertsShown, senseMode]);

  return (
    <>
      {senseMode !== SenseMode.Questionning && (
        <>
          <Button block disabled={isRecording && senseMode === SenseMode.TimedRecording} onClick={handleClickRecordingButton} type={isRecording ? 'default' : 'primary'}>
            {isRecording ? (
              <>
                <LoadingOutlined spin /> Stop recording
              </>
            ) : (
              'Start recording'
            )}
          </Button>
          <RecordNameModalDialog isOpen={isRecordModalOpen} setIsOpen={setIsRecordModalOpen} recordKey={recordKeyRef.current} isAutoSavedMeasurementEnabled={isAutoSavedMeasurementEnabled} />
        </>
      )}

      {isRecording && (
        <FlexCol style={{ textAlign: 'left', gap: 1, marginTop: 20 }}>
          <span>🔴 Recording... {renderElapsedTime()}</span>
          <span>
            <i>
              <b>Note</b>: leaving this page will stop the recording (saving what is recorded so far)
            </i>
          </span>
        </FlexCol>
      )}

      {isOdorResultDisplayed && <OdorResultsModal isOpen={isOdorResultDisplayed} setIsOpen={setIsOdorResultDisplayed} recordKeyRef={recordKeyRef} multiplicatorFactor={multiplicatorFactor} />}
    </>
  );
};

export default RecordingController;
